import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    LinearProgress,
    Chip,
    TextField,
    Stack,
    Fade,
    Collapse
} from '@mui/material';
import {
    QuestionMark,
    People,
    Schedule,
    RadioButtonUnchecked
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import ApiFetch from './ApiFetch';
import Dialog from './Dialog';
import { useTranslation } from "react-i18next";

const QuestionAnswer = (props) => {
    // スムーズなトランジション用のアニメーション定義（使用中のもののみ）
    const fadeInScale = keyframes`
        0% {
            opacity: 0;
            transform: scale(0.95) translateY(5px);
        }
        50% {
            opacity: 0.7;
            transform: scale(1.02) translateY(-2px);
        }
        100% {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    `;

    // フラットデザインのカスタムスタイル定義
    const FlatCard = styled(Card)(({ theme }) => ({
        backgroundColor: '#ffffff',
        borderRadius: 8,
        border: '1px solid #e0e0e0',
        marginBottom: 16,
        boxShadow: 'none',
        overflow: 'hidden',
        position: 'relative'
    }));

    const HeaderBox = styled(Box)(({ theme }) => ({
        backgroundColor: '#90caf9',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }));



    const OptionButton = styled(Button, {
        shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'mode'
    })(({ theme, isSelected, mode }) => ({
        backgroundColor: isSelected ? '#e3f2fd' : '#ffffff',
        border: isSelected ? '2px solid #1976d2' : '2px solid #2196f3',
        color: isSelected ? '#1565c0' : '#2196f3',
        borderRadius: 8,
        padding: theme.spacing(1.5, 2),
        fontSize: '1rem',
        fontWeight: 500,
        textTransform: 'none',
        justifyContent: 'flex-start',
        boxShadow: 'none',
        height: '64px',
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        transition: 'all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        transformOrigin: 'center',
        willChange: 'transform, box-shadow, background-color',
        cursor: 'pointer',
        overflow: 'hidden',
        boxSizing: 'border-box',
        outline: 'none',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(33, 150, 243, 0.1), transparent)',
            transition: 'left 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            zIndex: 1
        },
        // ホバー効果（デスクトップのみ）
        ...(mode === 'answer' && {
            '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {
                    backgroundColor: '#bbdefb',
                    border: '2px solid #1565c0',
                    color: '#0d47a1',
                    transform: 'translateY(-2px) scale(1.005)',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15), 0 2px 4px rgba(33, 150, 243, 0.12)',
                    '&::before': {
                        left: '100%'
                    },
                    '& .MuiSvgIcon-root': {
                        transform: 'scale(1.15) rotate(5deg)',
                        color: '#1565c0',
                        filter: 'drop-shadow(0 2px 4px rgba(33, 150, 243, 0.3))'
                    },
                    '& .option-text': {
                        transform: 'translateX(2px)',
                        fontWeight: 600
                    }
                }
            }
        }),
        // タッチデバイスでのタップ効果
        '@media (hover: none) and (pointer: coarse)': {
            '&:active': {
                backgroundColor: isSelected ? '#bbdefb' : '#e3f2fd',
                border: isSelected ? '2px solid #1565c0' : '2px solid #1565c0',
                color: '#0d47a1',
                transform: 'scale(0.98)'
            }
        },
        '&:active': {
            backgroundColor: isSelected ? '#bbdefb' : '#e3f2fd',
            border: isSelected ? '2px solid #1565c0' : '2px solid #1565c0',
            transform: 'translateY(-1px) scale(1.002)',
            boxShadow: '0 2px 6px rgba(33, 150, 243, 0.2)',
            '& .MuiSvgIcon-root': {
                transform: 'scale(1.05)',
                color: '#0d47a1'
            }
        },
        '&:focus-visible': {
            outline: '2px solid #2196f3',
            outlineOffset: '2px'
        },
        '&:disabled': {
            opacity: 0.7,
            cursor: 'not-allowed',
            transform: 'none',
            '&:hover': {
                transform: 'none',
                backgroundColor: '#ffffff',
                borderColor: '#2196f3'
            }
        },
        '& .MuiSvgIcon-root': {
            transition: 'all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            zIndex: 2,
            position: 'relative'
        },
        '& .option-text': {
            transition: 'all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            zIndex: 2,
            position: 'relative'
        }
    }));

    const ViewModeBox = styled(Box)(({ theme, animationDelay = 0 }) => ({
        backgroundColor: '#bdbdbd',
        borderRadius: 8,
        padding: theme.spacing(1.5, 2),
        color: 'white',
        height: '64px',
        width: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        opacity: 0,
        animation: `${fadeInScale} 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${animationDelay}ms forwards`,
        transformOrigin: 'center'
    }));

    const FlatLinearProgress = styled(LinearProgress)(({ theme }) => ({
        height: 6,
        borderRadius: 3,
        backgroundColor: 'rgba(255, 255, 255, 0.4)',
        overflow: 'hidden',
        '& .MuiLinearProgress-bar': {
            backgroundColor: '#2196f3',
            borderRadius: 3,
            transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
        }
    }));

    const StatsBox = styled(Box)(({ theme }) => ({
        backgroundColor: '#f5f5f5',
        borderRadius: 4,
        border: '1px solid #e0e0e0',
        padding: theme.spacing(1.5),
        marginTop: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }));

    const DeadlineBox = styled(Box)(({ theme }) => ({
        backgroundColor: '#fff3e0',
        borderRadius: 4,
        border: '1px solid #ffcc02',
        padding: theme.spacing(1),
        marginTop: theme.spacing(2)
    }));

	async function getQuestion(questionId){
        return await new ApiFetch().get(ApiFetch.API_PROTOCOL+"//"+ApiFetch.API_HOST+"/v1/question/info/"+questionId).then((r)=>r.json().then((json)=>{
            return json
        }))
    }
	async function setAnswer(questionId,optionNo){
        return await new ApiFetch().post(ApiFetch.API_PROTOCOL+"//"+ApiFetch.API_HOST+"/v1/question/info/"+questionId,{optionNo:optionNo}).then((r)=>r.json().then((json)=>{
            return json
        }))
    }
    // 状態管理
    const {t,i18n} = useTranslation()
    const [terms,setTerms] = useState("")
    const [questionId,setQuestionId] = useState(props.questionId)
    const [deadlineDatetime,setDeadlineDatetime] = useState()
    const [options,setOptions] = useState([])
    const [answerdCount,setAnswerdCount] = useState(0)
    const [mode,setMode] = useState("")
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [animationTrigger, setAnimationTrigger] = useState(0)
    // 選択中の選択肢番号をローカルstateで管理
    const [selectedOption, setSelectedOption] = useState(null)

    useEffect(() => {
        if(questionId !== undefined){
            getQuestion(questionId).then(r => {
                setMode(r.data.mode)
                if(r.data.mode === "view"){
                    setAnswerdCount(r.data.answered_count)
                    setOptions([
                        [r.data.option1, r.data.option1_count, r.data.selected_option === 0],
                        [r.data.option2, r.data.option2_count, r.data.selected_option === 1],
                        [r.data.option3, r.data.option3_count, r.data.selected_option === 2],
                        [r.data.option4, r.data.option4_count, r.data.selected_option === 3],
                        [r.data.option5, r.data.option5_count, r.data.selected_option === 4],
                        [r.data.option6, r.data.option6_count, r.data.selected_option === 5],
                        [r.data.option7, r.data.option7_count, r.data.selected_option === 6],
                        [r.data.option8, r.data.option8_count, r.data.selected_option === 7],
                        [r.data.option9, r.data.option9_count, r.data.selected_option === 8],
                        [r.data.option10, r.data.option10_count, r.data.selected_option === 9],
                    ].filter(opt => opt[0] && opt[0].trim().length > 0))
                } else {
                    setOptions([r.data.option1,r.data.option2,r.data.option3,r.data.option4,r.data.option5
                        ,r.data.option6,r.data.option7,r.data.option8,r.data.option9,r.data.option10])
                }
                setDeadlineDatetime(r.data.deadline_datetime)
            })
        }
    },[questionId])

    // ダイアログ
    const [show, setShow] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogMessage, setDialogMessage] = useState("");
    function dialog(title,message){
        setDialogTitle(title)
        setDialogMessage(message)
        setShow(true)
    }

    return (
        <FlatCard>
            <CardContent sx={{ p: 2 }}>
                <Dialog title={dialogTitle} message={dialogMessage} show={show} handleClose={() => setShow(false)} />
                
                {/* ヘッダー部分 */}
                {/* アニメーション削除済み - sx内keyframes使用問題を回避 */}

                {/* オプション部分 */}
                <Box sx={{ 
                    position: 'relative',
                    minHeight: mode === 'view' ? `${Math.max(options.filter(d => d[0]?.length > 0 || d?.length > 0).length * 72, 64)}px` : `${Math.max(options.filter(d => d?.length > 0).length * 72, 64)}px`,
                    transition: 'min-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    <Stack spacing={1}>
                        {options.map((d, i) => {
                            if(mode === "view" && (!d[0] || d[0].trim().length === 0)) return null
                            if(mode === "answer" && (!d || d.trim().length === 0)) return null
                            
                            const percentage = mode === "view" ? Math.round(d[1] / answerdCount * 100) : 0;

                            // viewモード時の最大カウントインデックスを算出
                            let maxCountIndex = -1;
                            if (mode === 'view') {
                                let max = -1;
                                options.forEach((opt, idx) => {
                                    if (opt[0]?.length > 0 && opt[1] > max) {
                                        max = opt[1];
                                        maxCountIndex = idx;
                                    }
                                });
                            }
                            
                            return (
                                <Fade 
                                    in={true} 
                                    timeout={600}
                                    style={{ 
                                        transitionDelay: `${i * 100}ms`,
                                        animationDelay: `${i * 100}ms`
                                    }}
                                    key={`${mode}-${i}-${animationTrigger}`}
                                >
                                    <Box 
                                        sx={{
                                            height: '64px',
                                            width: '100%',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            marginBottom: i === options.length - 1 ? 0 : 1
                                        }}
                                    >
                                        {mode === "view" ? (
                                            <ViewModeBox 
                                                animationDelay={i * 100}
                                                sx={{
                                                    backgroundColor: i === maxCountIndex ? '#1976d2' : '#bdbdbd',
                                                    color: i === maxCountIndex ? '#fffde7' : 'white',
                                                    border: i === maxCountIndex ? '2.5px solid #64b5f6' : 'none', // 青系で背景と馴染む色
                                                    boxShadow: i === maxCountIndex ? '0 0 12px 2px #64b5f655' : 'none',
                                                    ...((ViewModeBox?.sx)||{})
                                                }}
                                            >
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center',
                                                    height: '24px',
                                                    minHeight: '24px'
                                                }}>
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ 
                                                            fontWeight: 'bold', 
                                                            flex: 1,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            lineHeight: '24px'
                                                        }}
                                                    >
                                                        {d[0]}
                                                    </Typography>
                                                    <Chip 
                                                        label={`${percentage}%`}
                                                        size="small"
                                                        sx={{ 
                                                            bgcolor: '#ffffff',
                                                            color: '#333333',
                                                            fontWeight: 'bold',
                                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                                            width: '50px',
                                                            height: '20px',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{ 
                                                    height: '6px', 
                                                    mt: 1,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}>
                                                    <FlatLinearProgress 
                                                        variant="determinate" 
                                                        value={percentage}
                                                        sx={{ 
                                                            width: '100%',
                                                            '& .MuiLinearProgress-bar': {
                                                                transitionDelay: `${i * 150 + 400}ms`
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            </ViewModeBox>
                                        ) : (
                                            <OptionButton
                                                fullWidth
                                                variant="outlined"
                                                startIcon={<RadioButtonUnchecked sx={{ color: '#2196f3' }} />}
                                                isSelected={
                                                    mode === 'answer' ? selectedOption === i : i === maxCountIndex
                                                }
                                                mode={mode}
                                                onClick={() => {
                                                    if (mode === 'answer') {
                                                        setSelectedOption(i);
                                                        setIsTransitioning(true);
                                                        setAnswer(questionId, i).then(r => {
                                                            if(r.result){
                                                                setAnimationTrigger(prev => prev + 1);
                                                                setAnswerdCount(r.data.answered_count)
                                                                setMode(r.data.mode)
                                                                // view用に自分の選択肢にtrueをセット
                                                                setOptions([
                                                                    [r.data.option1, r.data.option1_count, r.data.selected_option === 0],
                                                                    [r.data.option2, r.data.option2_count, r.data.selected_option === 1],
                                                                    [r.data.option3, r.data.option3_count, r.data.selected_option === 2],
                                                                    [r.data.option4, r.data.option4_count, r.data.selected_option === 3],
                                                                    [r.data.option5, r.data.option5_count, r.data.selected_option === 4],
                                                                    [r.data.option6, r.data.option6_count, r.data.selected_option === 5],
                                                                    [r.data.option7, r.data.option7_count, r.data.selected_option === 6],
                                                                    [r.data.option8, r.data.option8_count, r.data.selected_option === 7],
                                                                    [r.data.option9, r.data.option9_count, r.data.selected_option === 8],
                                                                    [r.data.option10, r.data.option10_count, r.data.selected_option === 9],
                                                                ]);
                                                                setTimeout(() => {
                                                                    setIsTransitioning(false);
                                                                }, 800);
                                                            } else {
                                                                setIsTransitioning(false);
                                                                dialog(t("common.error"),r.errors)
                                                            }
                                                        })
                                                    }
                                                }}
                                                disabled={isTransitioning || mode !== 'answer'}
                                                sx={{
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start',
                                                    textAlign: 'left',
                                                    opacity: isTransitioning ? 0.7 : 1,
                                                    transition: 'opacity 0.3s ease',
                                                    filter: isTransitioning ? 'grayscale(0.3)' : 'none',
                                                    '&.MuiButton-root': {
                                                        border: '2px solid #2196f3 !important',
                                                        boxSizing: 'border-box !important'
                                                    },
                                                    '&.MuiButton-root:hover': {
                                                        border: '2px solid #1565c0 !important'
                                                    }
                                                }}
                                            >
                                                <Typography 
                                                    variant="body1" 
                                                    className="option-text"
                                                    sx={{ 
                                                        textAlign: 'left',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        flex: 1
                                                    }}
                                                >
                                                    {mode === 'view' ? d[0] : d}
                                                </Typography>
                                            </OptionButton>
                                        )}
                                    </Box>
                                </Fade>
                            )
                        })}
                    </Stack>
                </Box>

                {/* 統計情報 */}
                <Collapse in={mode === "view"} timeout={600}>
                    <StatsBox sx={{ 
                        opacity: mode === "view" ? 1 : 0,
                        transform: mode === "view" ? 'translateX(0)' : 'translateX(-20px)',
                        transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.8s'
                    }}>
                        <People sx={{ color: '#666666', mr: 1 }} />
                        <Typography variant="body2" sx={{ color: '#666666', fontWeight: 'bold' }}>
                            {t("questionnaire.answered")}: {answerdCount}
                        </Typography>
                    </StatsBox>
                </Collapse>

                {/* 締切情報 */}
                <Collapse in={!!deadlineDatetime} timeout={400}>
                    <DeadlineBox sx={{
                        opacity: deadlineDatetime ? 1 : 0,
                        transform: deadlineDatetime ? 'translateX(0)' : 'translateX(-20px)',
                        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.2s'
                    }}>
                        <TextField
                            label={t("common.deadline")}
                            value={deadlineDatetime}
                            disabled
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: <Schedule sx={{ mr: 1, color: '#ff9800' }} />
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: '#ffffff',
                                    '& fieldset': {
                                        borderColor: '#ffcc02'
                                    }
                                }
                            }}
                        />
                    </DeadlineBox>
                </Collapse>
            </CardContent>
        </FlatCard>
    )
}

export default QuestionAnswer;
